import { InstallGuideModal } from '../components/Install/InstallGuideModal.ts';
import { RulesModal } from '../components/Rules/RulesModal.ts';
import {
  canInstall,
  isIosSafari,
  isStandalone,
  subscribeInstall,
  triggerInstall,
} from '../lib/install-prompt.ts';
import { requestImmersiveLandscape } from '../lib/orientation-lock.ts';
import { isNative } from '../lib/runtime.ts';
import { cycleTheme, getTheme, subscribeTheme, type Theme } from '../lib/theme.ts';
import { router } from '../router.ts';
import type { MultiplayerService } from '../services/multiplayer.ts';
import { sessionStore, setNickname } from '../stores/index.ts';

/**
 * S1 메인 화면 — 닉네임 입력 + 룸 만들기/입장.
 */
export class MainPage {
  private root: HTMLElement;
  private multiplayer: MultiplayerService;
  private rulesModal: RulesModal | null = null;
  private installGuideModal: InstallGuideModal | null = null;
  private unsubscribeError: (() => void) | null = null;
  private unsubscribeRoomState: (() => void) | null = null;
  private unsubscribeInstall: (() => void) | null = null;
  private unsubscribeTheme: (() => void) | null = null;

  constructor(root: HTMLElement, multiplayer: MultiplayerService) {
    this.root = root;
    this.multiplayer = multiplayer;
  }

  mount(): void {
    this.unsubscribeError = this.multiplayer.onError((err) => {
      this.showError(err.message);
    });
    this.unsubscribeRoomState = this.multiplayer.onRoomState((state) => {
      if (state.status === 'IN_PROGRESS') {
        router.navigate({ name: 'play', code: state.roomCode });
      } else if (state.status === 'FINISHED') {
        router.navigate({ name: 'result', code: state.roomCode });
      } else {
        router.navigate({ name: 'room', code: state.roomCode });
      }
    });
    this.rulesModal = new RulesModal(document.body);
    this.rulesModal.mount();
    this.installGuideModal = new InstallGuideModal(document.body);
    this.installGuideModal.mount();
    this.unsubscribeInstall = subscribeInstall(() => this.refreshInstallButton());
    this.unsubscribeTheme = subscribeTheme(() => this.refreshThemeButton());
    this.render();
  }

  unmount(): void {
    this.unsubscribeError?.();
    this.unsubscribeRoomState?.();
    this.unsubscribeInstall?.();
    this.unsubscribeTheme?.();
    this.rulesModal?.unmount();
    this.rulesModal = null;
    this.installGuideModal?.unmount();
    this.installGuideModal = null;
  }

  private refreshThemeButton(): void {
    const btn = this.root.querySelector<HTMLButtonElement>('[data-role="theme-toggle"]');
    if (!btn) return;
    const theme = getTheme();
    btn.setAttribute('aria-label', themeAriaLabel(theme));
    btn.title = themeAriaLabel(theme);
    btn.innerHTML = themeIconSvg(theme);
  }

  private refreshInstallButton(): void {
    const btn = this.root.querySelector<HTMLButtonElement>('[data-role="install"]');
    const divider = this.root.querySelector<HTMLElement>('[data-role="install-divider"]');
    const show = this.shouldShowInstallButton();
    btn?.classList.toggle('hidden', !show);
    divider?.classList.toggle('hidden', !show);
  }

  private shouldShowInstallButton(): boolean {
    if (isNative() || isStandalone()) return false;
    return canInstall() || isIosSafari();
  }

  private showError(message: string): void {
    const el = this.root.querySelector<HTMLElement>('[data-role="error"]');
    if (el) {
      el.textContent = `⚠ ${message}`;
      el.classList.remove('hidden');
    }
  }

  private render(): void {
    const nickname = sessionStore.get().nickname;
    this.root.innerHTML = '';
    const el = document.createElement('main');
    el.className = 'min-h-screen safe-x safe-y flex flex-col items-center justify-center';
    el.style.setProperty('--page-pad-x', '1.5rem');
    el.style.setProperty('--page-pad-y', '1.5rem');
    const inner = document.createElement('div');
    inner.className = 'w-full max-w-md flex flex-col items-center gap-10';
    el.appendChild(inner);
    const cheatRow = import.meta.env.DEV
      ? `
        <button
          type="button"
          data-role="cheat-result"
          class="rounded-xl bg-fuchsia-700/60 hover:bg-fuchsia-600/60 text-fuchsia-100 py-2 text-xs font-medium ring-1 ring-fuchsia-500/40"
        >
          [dev] 결과 화면 미리보기
        </button>
      `
      : '';
    inner.innerHTML = `
      <div class="flex flex-col items-center">
        <h1 class="logo-tiles flex items-center gap-1 select-none" aria-label="Completto">
          ${['C', 'O', 'M', 'P', 'L', 'E', 'T', 'T', 'O']
            .map(
              (ch, i) =>
                `<span aria-hidden="true" class="logo-tile inline-flex items-center justify-center w-8 h-10 sm:w-9 sm:h-11 rounded-md bg-card-navy text-card-gold font-extrabold text-xl sm:text-2xl shadow-sm ring-1 ring-card-brass/40" style="transform: rotate(${i % 2 === 0 ? '-1.5deg' : '1.5deg'})">${ch}</span>`,
            )
            .join('')}
        </h1>
      </div>

      <div class="w-full flex flex-col gap-3">
        <label for="nickname-input" class="text-caption font-semibold uppercase tracking-wider text-wood-coffee/70">닉네임 <span class="font-mono normal-case tracking-normal text-wood-coffee/50">(1~12자)</span></label>
        <input
          id="nickname-input"
          type="text"
          maxlength="12"
          value="${escapeAttr(nickname)}"
          placeholder="닉네임을 입력하세요"
          class="rounded-xl bg-wood-paper ring-1 ring-wood-sand text-wood-ink px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-card-gold transition"
        />

        <button
          type="button"
          data-role="create"
          class="rounded-xl bg-card-gold hover:bg-card-brass text-wood-ink py-3 font-bold shadow-md shadow-card-gold/30 active:scale-[0.98] transition"
        >
          룸 만들기
        </button>
        <button
          type="button"
          data-role="join"
          class="rounded-xl bg-card-navy hover:bg-card-deep text-card-gold py-3 font-semibold transition"
        >
          코드로 입장
        </button>
        <button
          type="button"
          data-role="single"
          class="rounded-xl bg-transparent ring-1 ring-wood-sand hover:ring-wood-bark hover:bg-wood-paper text-wood-ink py-3 font-semibold transition"
        >
          혼자하기
        </button>

        <div class="flex items-center gap-2 mt-1">
          <button
            type="button"
            data-role="rules"
            class="flex-1 rounded-lg bg-transparent hover:bg-wood-paper/60 text-wood-coffee/80 py-2 text-xs font-medium transition"
          >
            게임 방법
          </button>
          <span data-role="install-divider" class="${this.shouldShowInstallButton() ? '' : 'hidden '}text-wood-sand/70 select-none" aria-hidden="true">·</span>
          <button
            type="button"
            data-role="install"
            class="${this.shouldShowInstallButton() ? '' : 'hidden '}flex-1 rounded-lg bg-transparent hover:bg-card-gold/10 text-card-brass py-2 text-xs font-medium transition"
          >
            앱으로 설치
          </button>
          <span class="text-wood-sand/70 select-none" aria-hidden="true">·</span>
          <button
            type="button"
            data-role="theme-toggle"
            aria-label="${themeAriaLabel(getTheme())}"
            title="${themeAriaLabel(getTheme())}"
            class="rounded-lg bg-transparent hover:bg-wood-paper/60 text-wood-coffee/80 p-2 transition flex items-center justify-center"
          >${themeIconSvg(getTheme())}</button>
        </div>
        ${cheatRow}

        <div data-role="error" class="hidden text-rose-400 text-sm text-center mt-2"></div>
      </div>
    `;
    this.root.appendChild(el);

    const input = el.querySelector<HTMLInputElement>('#nickname-input');
    input?.addEventListener('input', () => {
      setNickname(input.value.trim());
    });

    el.querySelector<HTMLButtonElement>('[data-role="create"]')?.addEventListener('click', () => {
      this.handleCreate();
    });
    el.querySelector<HTMLButtonElement>('[data-role="join"]')?.addEventListener('click', () => {
      router.navigate({ name: 'join' });
    });
    el.querySelector<HTMLButtonElement>('[data-role="single"]')?.addEventListener('click', () => {
      void requestImmersiveLandscape();
      router.navigate({ name: 'single' });
    });
    el.querySelector<HTMLButtonElement>('[data-role="rules"]')?.addEventListener('click', () => {
      this.rulesModal?.open();
    });
    el.querySelector<HTMLButtonElement>('[data-role="install"]')?.addEventListener('click', () => {
      void this.handleInstall();
    });
    el.querySelector<HTMLButtonElement>('[data-role="theme-toggle"]')?.addEventListener(
      'click',
      () => {
        cycleTheme();
      },
    );
    if (import.meta.env.DEV) {
      el.querySelector<HTMLButtonElement>('[data-role="cheat-result"]')?.addEventListener(
        'click',
        () => this.handleCheatResult(),
      );
    }
  }

  private handleCheatResult(): void {
    if (!import.meta.env.DEV) return;
    const nickname = sessionStore.get().nickname.trim();
    if (nickname.length < 1) {
      this.showError('닉네임을 입력하세요');
      return;
    }
    sessionStorage.setItem('completto:cheat-finish', '1');
    router.navigate({ name: 'single' });
  }

  private async handleInstall(): Promise<void> {
    if (canInstall()) {
      await triggerInstall();
      return;
    }
    if (isIosSafari()) {
      this.installGuideModal?.open();
    }
  }

  private handleCreate(): void {
    const nickname = sessionStore.get().nickname.trim();
    if (nickname.length < 1) {
      this.showError('닉네임을 입력하세요');
      return;
    }
    this.multiplayer.createRoom();
  }
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function themeAriaLabel(theme: Theme): string {
  if (theme === 'system') return '테마: 시스템 (탭하여 변경)';
  if (theme === 'light') return '테마: 라이트 (탭하여 변경)';
  return '테마: 다크 (탭하여 변경)';
}

function themeIconSvg(theme: Theme): string {
  const common =
    'width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';
  if (theme === 'light') {
    return `<svg ${common}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>`;
  }
  if (theme === 'dark') {
    return `<svg ${common}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;
  }
  return `<svg ${common}><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 21h8M12 17v4"/></svg>`;
}
